import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Select,
  Space,
  message,
  Modal,
} from 'antd';
import {
  UserSwitchOutlined,
  CalendarOutlined,
  FileTextOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';
import { Divider } from 'antd';
import { downloadInvoicePdf } from '../utils/invoiceDownload';

const { Title, Text } = Typography;

const statusColors = {
  pending: 'gold',
  confirmed: 'blue',
  completed: 'green',
  cancelled: 'red',
};

const statusLabels = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const AdminBookings = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [assigning, setAssigning] = useState(null);
  const [providerId, setProviderId] = useState(null);
  const [billTarget, setBillTarget] = useState(null);
  const [billingType, setBillingType] = useState('standard');

  const { data: bookingsResponse, isLoading } = useQuery(
    ['admin-bookings', statusFilter, page],
    async () => {
      const response = await api.get('/admin/bookings', {
        params: { status: statusFilter || undefined, page },
      });
      return response.data;
    },
  );

  const { data: providersResponse } = useQuery('admin-providers', async () => {
    const response = await api.get('/admin/providers');
    return response.data;
  });

  const bookings = bookingsResponse?.data || [];
  const total = bookingsResponse?.total || 0;
  const providers = Array.isArray(providersResponse) ? providersResponse : providersResponse?.data || [];

  const assignMutation = useMutation(
    ({ bookingId, newProviderId }) =>
      api.put(`/admin/bookings/${bookingId}/provider`, { provider_id: newProviderId }),
    {
      onSuccess: () => {
        message.success('Provider assigned successfully');
        setAssigning(null);
        setProviderId(null);
        queryClient.invalidateQueries('admin-bookings');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to assign provider');
      },
    },
  );

  const generateBillMutation = useMutation(
    (bookingId) =>
      api.post(`/admin/billing/bookings/${bookingId}/generate-bill`, {
        billing_type: billingType,
      }),
    {
      onSuccess: (response) => {
        const body = response?.data || response;
        const invoice = body?.data;
        if (body?.already_exists && invoice) {
          const label = invoice.invoice_number || `#${invoice.id}`;
          message.info(`Invoice ${label} already exists for this booking`, 5);
          downloadInvoicePdf(invoice, 'invoice');
        } else {
          message.success(`Bill generated — ${invoice?.invoice_number || 'invoice created'}`);
        }
        setBillTarget(null);
        setBillingType('standard');
        queryClient.invalidateQueries('admin-bookings');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to generate bill');
      },
    },
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 180,
      render: (_, record) => (
        <div>
          <Text strong>{record.user?.name || '-'}</Text>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>{record.user?.email || ''}</div>
        </div>
      ),
    },
    {
      title: 'Service',
      key: 'service',
      width: 200,
      render: (_, record) => record.product_service?.name || `#${record.product_service_id}`,
    },
    {
      title: 'Provider',
      key: 'provider',
      render: (_, record) => {
        const isAdmin = record.provider && record.provider.name && (record.provider.email || '').includes('admin');
        return (
          <Space direction="vertical" size={0}>
            <Text>{record.provider?.name || 'Unassigned'}</Text>
            {isAdmin && <Tag color="purple" style={{ marginTop: 2 }}>Default (Admin)</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Schedule',
      key: 'schedule',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.booking_date ? dayjs(record.booking_date).format('DD MMM YYYY') : '-'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.booking_time ? dayjs(record.booking_time).format('h:mm A') : '-'}</Text>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      width: 90,
      render: (amount) => (Number(amount) === 0 ? <Tag color="green">FREE</Tag> : `NPR ${amount}`),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (status) => <Tag color={statusColors[status] || 'default'}>{statusLabels[status] || status}</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 260,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<UserSwitchOutlined />}
            onClick={() => {
              setAssigning(record);
              setProviderId(record.provider_id);
            }}
          >
            Assign Provider
          </Button>
          <Button
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => {
              setBillTarget(record);
              setBillingType('standard');
            }}
          >
            Generate Bill
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <CalendarOutlined /> Service Bookings
        </Title>
        <Select
          allowClear
          placeholder="Filter by status"
          style={{ width: 180 }}
          value={statusFilter || undefined}
          onChange={(v) => {
            setStatusFilter(v || '');
            setPage(1);
          }}
          options={Object.entries(statusLabels).map(([value, label]) => ({ value, label }))}
        />
      </div>

      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={bookings}
          pagination={{
            current: page,
            pageSize: 15,
            total,
            onChange: setPage,
            showSizeChanger: false,
          }}
        />
      </Card>

      <Modal
        title={`Assign Provider — Booking #${assigning?.id || ''}`}
        open={!!assigning}
        onCancel={() => setAssigning(null)}
        footer={null}
        width={400}
      >
        {assigning && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              {assigning.product_service?.name} · {assigning.user?.name}
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select provider"
              value={providerId}
              onChange={setProviderId}
              showSearch
              optionFilterProp="label"
              options={providers.map((p) => ({ value: p.id, label: p.name || p.full_name || p.email }))}
            />
            <Button
              type="primary"
              block
              loading={assignMutation.isLoading}
              disabled={!providerId}
              onClick={() => assignMutation.mutate({ bookingId: assigning.id, newProviderId: providerId })}
            >
              Assign
            </Button>
          </Space>
        )}
      </Modal>

      <Modal
        title={`Generate Bill — Booking #${billTarget?.id || ''}`}
        open={!!billTarget}
        onCancel={() => {
          setBillTarget(null);
          setBillingType('standard');
        }}
        footer={null}
        width={480}
      >
        {billTarget && (
          <div>
            <p style={{ marginBottom: 16 }}>
              <strong>Service:</strong> {billTarget.product_service?.name || `#${billTarget.product_service_id}`}
            </p>
            <p style={{ marginBottom: 16 }}>
              <strong>Customer:</strong> {billTarget.user?.name || '-'} ({billTarget.user?.email || '-'})
            </p>
            <p style={{ marginBottom: 16 }}>
              <strong>Amount:</strong> NPR {Number(billTarget.total_amount).toFixed(2)}
            </p>

            <Divider>Select Billing Type</Divider>

            <div style={{ display: 'flex', gap: 16 }}>
              <Card
                hoverable
                style={{
                  flex: 1,
                  border: billingType === 'standard' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  borderRadius: 8,
                }}
                onClick={() => setBillingType('standard')}
              >
                <div style={{ textAlign: 'center' }}>
                  <FileTextOutlined
                    style={{ fontSize: 32, color: billingType === 'standard' ? '#1890ff' : '#999' }}
                  />
                  <h4 style={{ marginTop: 8 }}>Standard Billing</h4>
                  <p style={{ color: '#666', fontSize: 12 }}>No VAT</p>
                </div>
              </Card>

              <Card
                hoverable
                style={{
                  flex: 1,
                  border: billingType === 'vat' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  borderRadius: 8,
                }}
                onClick={() => setBillingType('vat')}
              >
                <div style={{ textAlign: 'center' }}>
                  <DollarOutlined
                    style={{ fontSize: 32, color: billingType === 'vat' ? '#1890ff' : '#999' }}
                  />
                  <h4 style={{ marginTop: 8 }}>VAT Billing</h4>
                  <p style={{ color: '#666', fontSize: 12 }}>13% VAT on products</p>
                </div>
              </Card>
            </div>

            <Button
              type="primary"
              block
              style={{ marginTop: 20 }}
              loading={generateBillMutation.isLoading}
              onClick={() => generateBillMutation.mutate(billTarget.id)}
            >
              Generate Bill
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminBookings;
